import { useState } from 'react';
import { MantineTabs } from '@dealstack/frontend/shared/components/MantineTabs/MantineTabs.tsx';
import { MantineTabProps } from '@dealstack/frontend/shared/components/MantineTabs/MantineTab.types';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { ToggleRequirementItem } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/components/ToggleRequirementItem.tsx';
import { SegmentedRequirementItem } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/components/SegmentedRequirementItem.tsx';
import { CertificationTextEditor } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/components/CertificationTextEditor';
import { BankAccountApprovalMode } from '@dealstack/shared/lib/generated';
import { KycRequirementsList } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/components/KycRequirementsList';
import {
	KycSection,
	KycSettingsContainer,
	StyledCard,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/styles.ts';
import {
	ENTITY_TABS,
	KycSettingsType,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/types.ts';
import { useKycCertificationEnabled } from '@dealstack/frontend/shared/hooks/useKycCertificationEnabled/useKycCertificationEnabled.hook.ts';

interface Props {
	kycSettings: KycSettingsType;
	/** Only the organisation-defaults tier passes this; the programme tier reads its route. */
	organizationId?: string;
}

export const KycSettingsView = ({ kycSettings, organizationId }: Props) => {
	const tMipManage = useTranslationMipManage();
	const [activeTab, setActiveTab] = useState<ENTITY_TABS>(ENTITY_TABS.INDIVIDUAL);

	const { settings, toggleSetting, bankAccountApproval, setBankAccountApproval, hasPermission } =
		kycSettings;

	const bankAccountApprovalOptions = Object.values(BankAccountApprovalMode).map((mode) => ({
		value: mode,
		label: tMipManage(`kyc_settings.bank_account_approval.option.${mode}`),
	}));

	// The feature switch, as opposed to `isCertificationEnabled` below, which is whether this
	// programme happens to ask for a certified copy of anything.
	const { certificationEnabled: certificationFeatureEnabled } = useKycCertificationEnabled({
		organizationId,
	});

	const isCertificationEnabled =
		settings.proofOfIdentityCertificationRequired.checked ||
		settings.proofOfAddressCertificationRequired.checked ||
		settings.proofOfInvestmentVehicleCertificationRequired.checked;

	const tabsConfig: MantineTabProps<ENTITY_TABS>[] = [
		{
			label: tMipManage('kyc_settings.label.individual'),
			value: ENTITY_TABS.INDIVIDUAL,
			icon: 'information',
		},
		{
			label: tMipManage('kyc_settings.label.vehicle'),
			value: ENTITY_TABS.INVESTMENT_VEHICLE,
			icon: 'derived_commuted_structure',
		},
	];

	return (
		<KycSettingsContainer>
			<KycSection $noPadding>
				<ToggleRequirementItem
					title={tMipManage('kyc_settings.kyc_required.label')}
					description={tMipManage('kyc_settings.kyc_required.description')}
					checked={settings.kycRequired.checked}
					disabled={settings.kycRequired.disabled || !hasPermission}
					onToggle={() => toggleSetting('kycRequired')}
				/>
				<SegmentedRequirementItem
					title={tMipManage('kyc_settings.bank_account_approval.label')}
					description={tMipManage('kyc_settings.bank_account_approval.description')}
					value={bankAccountApproval.value}
					options={bankAccountApprovalOptions}
					disabled={bankAccountApproval.disabled || !hasPermission}
					onChange={setBankAccountApproval}
				/>
				{/* Always mounted while the feature is on: hiding it moved a control above the
				    tabs whenever a proof toggle inside them cleared the last certification, and
				    stranded any saved wording. */}
				{certificationFeatureEnabled && (
					<CertificationTextEditor
						source={kycSettings.certificationText}
						disabled={!hasPermission || !isCertificationEnabled}
						isUnused={!isCertificationEnabled}
					/>
				)}
			</KycSection>
			<StyledCard>
				<MantineTabs
					tabs={tabsConfig}
					panels={[]}
					onlyDividerBorder={true}
					value={activeTab}
					onTabChange={(value) => setActiveTab(value as ENTITY_TABS)}
				>
					<KycRequirementsList
						type={activeTab}
						kycSettings={kycSettings}
						certificationEnabled={certificationFeatureEnabled}
					/>
				</MantineTabs>
			</StyledCard>
		</KycSettingsContainer>
	);
};
